import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

type Academy = { id: number; name: string };
type PublicProfile = any & { academies: Academy[] };

export function usePublicProfile(username: string | undefined) {
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!username) return;
    setLoading(true);
    setError(null);
    (async () => {
      // Fetch main profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*, business_type:business_types(name)')
        .eq('username', username)
        .single();
      if (profileError || !profileData) {
        setError(profileError?.message || 'Profile not found');
        setProfile(null);
        setLoading(false);
        return;
      }
      const profileId = profileData.id;

      // Fetch main academy
      let academies: Academy[] = [];
      
      if (profileData?.main_academy_id) {
        const { data: academyData, error: academyError } = await supabase
          .from('academies')
          .select('id, name')
          .eq('id', profileData.main_academy_id)
          .limit(1);
        
        if (academyError) {
          // Academy fetch error - continue without academy data
        }
        
        academies = academyData || [];
      }

      // Fetch interests
      const { data: hobbyData } = await supabase
        .from('profile_hobbies')
        .select('hobby:hobbies(name)')
        .eq('profile_id', profileId);
      const interests = hobbyData?.map((h: any) => h.hobby?.name).filter(Boolean) || [];

      // Fetch real technique categories
      const { data: categoriesData } = await supabase
        .from('technique_categories')
        .select('name');
      const techniqueCategories = categoriesData?.map((c: any) => c.name) || [];

      // Fetch favorite techniques grouped by category
      const { data: favTechData } = await supabase
        .from('profile_favorite_techniques')
        .select('technique:techniques(name, category:technique_categories(name))')
        .eq('profile_id', profileId);
      const favoriteTechniques: Record<string, string[]> = {};
      techniqueCategories.forEach((cat: string) => { favoriteTechniques[cat] = []; });
      favTechData?.forEach((item: any) => {
        const cat = item.technique?.category?.name || 'Other';
        if (!favoriteTechniques[cat]) favoriteTechniques[cat] = [];
        if (item.technique?.name) favoriteTechniques[cat].push(item.technique.name);
      });

      const finalProfile = {
        ...profileData,
        academies,
        interests,
        technique_categories: techniqueCategories,
        favorite_techniques: favoriteTechniques,
        business_type: profileData.business_type?.name || '',
      };
      
      setProfile(finalProfile);
      setLoading(false);
    })();
  }, [username]);

  return { profile, loading, error };
} 